



































































































































import { computed, defineComponent, getCurrentInstance, PropType, ref, toRefs, watch } from "@vue/composition-api";
import { ContentPriceSubjectTypeEnum } from "@scrile/api-provider/dist/api/ContentPriceProvider";
import { FileData } from "@scrile/api-provider/dist/api/FileProvider";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { PurchasedFile } from "@scrile/api-provider/dist/api/PurchasedFileProvider";
import { Subject } from "@scrile/api-provider/dist/api/SubjectProvider";
import {
  VaultFileSearchResult,
  VaultCategory,
  VaultFileUsageSubjectTypeEnum,
} from "@scrile/api-provider/dist/api/VaultsProvider";
import { UploadFileData } from "@scrile/tools/dist/lib/FileUploader";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { getFileMimeType, isVideoFile } from "@/lib/fileHelpers";
import FileUploader, { calculateProgress, showDefaultUploadError } from "@/lib/FileUploader";
import { $t } from "@/lib/i18nHelpers";
import { UploadVaultFile } from "@/types";
import { EVENTS } from "@/const";
import BaseButton from "@/components/BaseButton";
import BaseDropZone from "@/components/BaseDropZone";
import BaseIconButton from "@/components/BaseIconButton";
import BaseThumbnailButton from "@/components/BaseThumbnailButton";
import MediaFileAddButton from "@/components/MediaFileAddButton";
import PageTitle from "@/components/PageTitle";
import EditCaptionConfirm from "./components/EditCaptionConfirm";
import VaultAiBadge from "./components/VaultAiBadge";
import VaultCategoryList from "./components/VaultCategoryList";
import VaultCreateCategoryModal from "./components/VaultCreateCategoryModal";
import VaultFilesList from "./components/VaultFilesList";
import VaultManageView from "./components/VaultManageView";
import VaultMenu from "./components/VaultMenu";
import VaultMoveConfirm from "./components/VaultMoveConfirm";
import { useFilterVaultFiles } from "./hooks/useFilterVaultFiles";
import { useVaultFiles } from "./hooks/useVaultFiles";
import { PurchasedFilesMap, SentFilesMap } from "./types";

export default defineComponent({
  name: "VaultController",
  components: {
    VaultAiBadge,
    EditCaptionConfirm,
    PageTitle,
    VaultMenu,
    BaseIconButton,
    BaseButton,
    VaultCategoryList,
    BaseThumbnailButton,
    VaultManageView,
    BaseDropZone,
    VaultFilesList,
    MediaFileAddButton,
    VaultCreateCategoryModal,
    VaultMoveConfirm,
  },
  props: {
    selectMode: { type: Boolean, default: false },
    multiple: { type: Boolean, default: false },
    maxFiles: { type: Number, required: false },
    enableDropZone: { type: Boolean, default: false },
    stickyButtons: { type: Boolean, default: true },
    recipientId: { type: String },
    filterSubject: { type: Object as PropType<Subject<VaultFileUsageSubjectTypeEnum.MESSAGE_THREAD>>, default: null },
    enableFilesCaption: { type: Boolean, default: false },
    paymentPackages: { type: Array as PropType<PaymentPackage[]>, default: () => [] },
  },
  setup(props, { emit }) {
    const DEFAULT_SIZE = 20;
    const vm = getCurrentInstance();
    const { authUser, authorities } = useAuthUser();
    const selectedCategory = ref<VaultCategory | null>(null);
    const vaultFileSearchResult = ref<VaultFileSearchResult | null>(null);
    const vaultCategories = ref<VaultCategory[]>([]);
    const vaultFiles = ref<UploadVaultFile[]>([]);
    const sentFiles = ref<FileData[]>([]);
    const purchasedFiles = ref<PurchasedFile[]>([]);
    const showCreateModal = ref(false);
    const scroll = ref(true);
    const manage = ref(false);
    const allowAi = ref(false);
    const selectedFile = ref<UploadVaultFile | null>(null);
    const updateFileProcessing = ref(false);

    const { filterSubject } = toRefs(props);
    const { filterByUnused, showUnusedFiles } = useFilterVaultFiles(filterSubject);

    const purchasedFileMap = computed<PurchasedFilesMap>(() => {
      return purchasedFiles.value.reduce<PurchasedFilesMap>((acc, i) => {
        acc[i.uuid] = true;
        return acc;
      }, {});
    });

    const sentFileMap = computed<SentFilesMap>(() => {
      return sentFiles.value.reduce<SentFilesMap>((acc, i) => {
        acc[i.uuid] = true;
        return acc;
      }, {});
    });

    const allowUpload = computed(
      () => props.enableDropZone && selectedCategory.value && !selectedCategory.value.system
    );

    const getFiles = async (
      categoryIds: string[] | undefined = undefined,
      page: number = 0,
      replace: boolean = true,
      oldPage?: number
    ) => {
      try {
        scroll.value = false;
        const response = await providers.VaultsProvider.search({
          data: {
            userId: authUser.value?.id || "",
            categoryIds,
            page,
            size: oldPage !== undefined ? DEFAULT_SIZE * (oldPage + 1) : DEFAULT_SIZE,
            unusedIn: filterByUnused.value,
            sort: "LAST_USE_TIME,DESC",
          },
        });
        vaultFileSearchResult.value = {
          ...response,
          page: oldPage !== undefined ? oldPage : response.page,
          result:
            replace || !vaultFileSearchResult.value
              ? response.result
              : vaultFileSearchResult.value.result.concat(response.result),
        };
        vaultFiles.value = vaultFileSearchResult.value.result;
        if (response.result.length > 0 && !showUnusedFiles.value && !!props.recipientId && props.filterSubject) {
          const sentFilesResponse = await providers.FileProvider.search({
            data: {
              uuids: response.result.map(i => i.uuid),
              usedIn: props.filterSubject,
            },
          });
          sentFiles.value = replace ? sentFilesResponse : sentFiles.value.concat(sentFilesResponse);
          const purchasedFilesResponse = await providers.PurchasedFileProvider.search({
            data: {
              ownerId: authUser.value?.id ?? "",
              userId: props.recipientId,
              uuids: response.result.map(i => i.uuid),
              page: 0,
              size: response.result.length,
            },
          });
          purchasedFiles.value = replace
            ? purchasedFilesResponse.result
            : purchasedFiles.value.concat(purchasedFilesResponse.result);
        }
      } finally {
        scroll.value = vaultFileSearchResult.value?.hasNextPage ?? false;
      }
    };

    const getAiUserSettings = async () => {
      if (!authUser.value || !checkAuthorities(authorities.value, ["getMyAiUserSettings"])) return;
      const response = await providers.AiSettingsProvider.userSettingsSearch({ userId: authUser.value.id });
      allowAi.value = response.enabled;
    };

    const getCategories = async () => {
      vaultCategories.value = await providers.VaultsProvider.searchCategories(authUser.value?.id ?? "", [
        "name",
        "id",
        "system",
        { files: ["id", "urlPart", "contentType"] },
        "totalFilesCount",
        "ai",
        "keywords",
      ]);

      if (selectedCategory.value) {
        vm?.$set(
          selectedCategory.value,
          "files",
          vaultCategories.value.find(i => i.id === selectedCategory.value?.id)?.files
        );
      }
    };

    const updateFiles = async (categoryId: string) => {
      if (!vaultFileSearchResult.value?.hasNextPage) {
        if (vaultFileSearchResult.value) {
          vm?.$set(vaultFileSearchResult.value, "result", vaultFiles.value);
        }
        return;
      }
      const page = (vaultFileSearchResult.value?.page ?? 0) + 1;
      try {
        scroll.value = false;
        const response = await providers.VaultsProvider.search({
          data: {
            userId: authUser.value?.id || "",
            categoryIds: [categoryId],
            page: 0,
            size: DEFAULT_SIZE * page,
            unusedIn: filterByUnused.value,
            sort: "LAST_USE_TIME,DESC",
          },
        });
        vm?.$set(vaultFileSearchResult.value, "result", response.result);
        vm?.$set(vaultFileSearchResult.value, "hasNextPage", response.hasNextPage);
        vaultFiles.value = vaultFileSearchResult.value.result;
      } finally {
        scroll.value = vaultFileSearchResult.value?.hasNextPage ?? false;
      }
    };
    const onDeleteFiles = async (files: UploadVaultFile[]) => {
      const categoryId = files[0].categoryIds[0];
      vaultFiles.value = vaultFiles.value.filter(i => !files.find(file => file.id === i.id));
      await updateFiles(categoryId);
      await getCategories();
      manage.value = false;
    };

    const getNextPage = async (cb: Function) => {
      const categoryIds = selectedCategory.value ? [selectedCategory.value.id] : undefined;
      const nextPage = (vaultFileSearchResult.value?.page ?? 0) + 1;
      await getFiles(categoryIds, nextPage, false);
      cb();
    };

    const addFiles = async (files: FileList) => {
      if (!authUser.value || !selectedCategory.value) return;
      const uploadFiles: UploadFileData[] = [];
      const uploadVaultFiles: UploadVaultFile[] = [];
      const uploadUrls = await providers.VaultsProvider.getUploadUrls(files.length);

      for (const [index, file] of Array.from(files).entries()) {
        const vaultFile: UploadVaultFile = {
          id: uploadUrls[index].uuid,
          userId: authUser.value.id,
          uuid: uploadUrls[index].uuid,
          contentType: getFileMimeType(file),
          categoryIds: [selectedCategory.value.id],
          urlPart: "",
          createTime: "",
          lastUseTime: "",
          metadata: {},
          caption: "",
          pricePackageId: null,
        };
        uploadVaultFiles.push(vaultFile);
        uploadFiles.push({
          file,
          fileType: getFileMimeType(file),
          urlPart: uploadUrls[index].urlPart,
          id: `${uploadUrls[index].uuid}`,
          onSuccess: async () => {
            const vaultFile: UploadVaultFile = await providers.VaultsProvider.createFile({
              data: {
                userId: authUser.value?.id || "",
                uuid: uploadUrls[index].uuid,
                categoryId: selectedCategory.value?.id || "",
              },
            });
            const categoryIndex = vaultCategories.value.findIndex(c => c.id === selectedCategory.value?.id);
            if (categoryIndex > -1) {
              if ((vaultCategories.value[categoryIndex].files?.length as number) === 4) {
                vaultCategories.value[categoryIndex].files?.splice(0, 1, vaultFile);
              } else {
                vaultCategories.value[categoryIndex].files?.unshift(vaultFile);
              }
            }
            vaultFiles.value = vaultFiles.value.filter(f => f.uuid !== vaultFile.uuid);
            vaultFiles.value = [vaultFile].concat(vaultFiles.value);
            await getCategories();
          },
          onUploadProgress: p => {
            const fileIndex = vaultFiles.value.findIndex(f => f.uuid === uploadUrls[index].uuid);
            if (fileIndex > -1) {
              vaultFiles.value.splice(fileIndex, 1, {
                ...vaultFile,
                uploadProgress: calculateProgress(p.total ?? 0, p?.loaded ?? 0),
              });
            }
          },
          onError: () => {
            showDefaultUploadError();
          },
        });
      }

      let endVaultFiles: UploadVaultFile[];
      if (vaultFileSearchResult.value?.hasNextPage) {
        endVaultFiles = vaultFiles.value.slice(0, vaultFiles.value.length - uploadFiles.length);
        vaultFileSearchResult.value.result = endVaultFiles;
      } else {
        endVaultFiles = vaultFiles.value;
      }
      vaultFiles.value = [...uploadVaultFiles, ...endVaultFiles];
      FileUploader.addToQueue(uploadFiles);
    };

    const onUnusedFilterClick = async () => {
      showUnusedFiles.value = !showUnusedFiles.value;
      const selectedCategoryIds = selectedCategory.value?.id ? [selectedCategory.value.id] : undefined;
      await getFiles(selectedCategoryIds, 0, true, vaultFileSearchResult.value?.page);
    };

    const onUpdate = async (category?: VaultCategory) => {
      if (category) {
        const currentId = vaultCategories.value.findIndex(c => c.id === category?.id);
        selectedCategory.value = { ...category, files: selectedCategory.value?.files };
        if (currentId === -1) {
          vaultCategories.value = [...vaultCategories.value, category];
          await getCategories();
        } else {
          vaultCategories.value.splice(currentId, 1, { ...vaultCategories.value[currentId], ...category });
        }
      }
    };

    const onDelete = () => {
      vaultCategories.value = vaultCategories.value.filter(c => c.id !== selectedCategory.value?.id);
      selectedCategory.value = null;
    };

    const { movedFiles, moveLoading, showMoveFilesConfirm, moveFiles, setMovedFiles, setCallBack } = useVaultFiles(
      authUser,
      selectedCategory
    );

    const toggleShowMoveConfirm = (status: boolean): void => {
      showMoveFilesConfirm.value = status;
    };
    const onMoveConfirmClose = (): void => {
      if (moveLoading.value) return;
      showMoveFilesConfirm.value = false;
    };
    const onMoveConfirmConfirm = async (categoryId: string): Promise<void> => {
      if (vaultFileSearchResult.value) {
        vaultFileSearchResult.value.result = vaultFiles.value.filter(
          i => !movedFiles.value.find(file => file.id === i.id)
        );
      }
      await moveFiles(categoryId);
      toggleShowMoveConfirm(false);
      emitter.emit(EVENTS.NOTIFICATION_SHOW, {
        text: $t(`{filesCount} ${movedFiles.value.length === 1 ? "has" : "have"} been moved`, {
          filesCount: vm?.$tc("file", movedFiles.value.length),
        }),
      });
      const selectedCategoryIds = selectedCategory.value?.id ? [selectedCategory.value.id] : undefined;
      await getFiles(selectedCategoryIds, 0, true);
      await getCategories();
      manage.value = false;
    };

    const onFilesMove = (files: UploadVaultFile[], cleanCheckedFiles?: () => void): void => {
      if (movedFiles.value.length > 0) setMovedFiles([]);
      setMovedFiles(files);
      cleanCheckedFiles && setCallBack(cleanCheckedFiles);
      toggleShowMoveConfirm(true);
    };

    const onEditCaptionClick = (file: UploadVaultFile) => {
      selectedFile.value = file;
    };

    const onCloseEditCaptionConfirm = () => {
      selectedFile.value = null;
    };

    const onUpdatePriceCaption = async ({
      file,
      caption,
      pricePackageId,
      cb,
    }: {
      file: UploadVaultFile;
      caption: string;
      pricePackageId: string;
      cb?: () => void;
    }) => {
      try {
        const promises = [
          providers.ContentPriceProvider.update({
            data: {
              subject: {
                subjectId: file.id,
                subjectType: ContentPriceSubjectTypeEnum.VAULT_FILE,
              },
              packageId: pricePackageId,
            },
          }).then(() => selectedFile.value && (selectedFile.value.pricePackageId = pricePackageId)),
          providers.VaultsProvider.updateFile({
            data: {
              id: file.id,
              caption,
            },
          }).then(() => selectedFile.value && (selectedFile.value.caption = caption)),
        ];
        await Promise.all(promises);
      } finally {
        cb && cb();
        onCloseEditCaptionConfirm();
      }
    };

    const onBack = () => {
      if (manage.value) {
        manage.value = false;
        return;
      }
      selectedCategory.value = null;
    };

    getAiUserSettings();

    watch(
      selectedCategory,
      async currentValue => {
        vaultFiles.value = [];
        sentFiles.value = [];
        purchasedFiles.value = [];
        vaultFileSearchResult.value = null;
        try {
          emit("loading", true);
          emit("showCategory", !!currentValue);
          let ids = currentValue ? [currentValue.id] : undefined;
          let requests = [getFiles(ids)];
          if (!currentValue && vaultCategories.value.length === 0) {
            requests.push(getCategories());
          }
          await Promise.all(requests);
        } finally {
          emit("loading", false);
        }
      },
      { immediate: true }
    );

    return {
      scroll,
      allowAi,
      manage,
      selectedCategory,
      showCreateModal,
      isVideoFile,
      getNextPage,
      getCategories,
      onUpdate,
      onDelete,
      onDeleteFiles,
      onBack,
      onFilesMove,
      onMoveConfirmClose,
      onMoveConfirmConfirm,
      showMoveFilesConfirm,
      vaultFileSearchResult,
      vaultCategories,
      vaultFiles,
      addFiles,
      onUnusedFilterClick,
      allowUpload,
      showUnusedFiles,
      purchasedFileMap,
      sentFileMap,
      onEditCaptionClick,
      onCloseEditCaptionConfirm,
      selectedFile,
      updateFileProcessing,
      onUpdatePriceCaption,
    };
  },
});
